import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { format } from 'date-fns';
import axios from '../utils/axiosConfig';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const postsPerPage = 3;

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/v2/blog');
        setPosts(response.data.blogs || []);
      } catch (err) {
        setError('Failed to load blog posts. Please try again later.');
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = currentPage * postsPerPage;
  const visiblePosts = posts.slice(startIndex, startIndex + postsPerPage);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  if (loading) {
    return (
      <Container sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 10 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ py: 6, bgcolor: '#f5f5f5', mt: 8 }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 1,
            color: '#755985',
            fontWeight: 700
          }}
        >
          Narrow Gate Blog
        </Typography>
        <Typography
          variant="h5"
          align="center"
          sx={{
            mb: 6,
            color: 'text.secondary',
            maxWidth: 800,
            mx: 'auto'
          }}
        >
          Stay updated with the latest basketball training insights and news
        </Typography>

        <Grid container spacing={4}>
          {visiblePosts.length > 0 ? (
            visiblePosts.map((post) => (
              <Grid item xs={12} md={6} lg={4} key={post._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px -12px rgba(117,89,133,0.2)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="180"
                    image={post.image || '/images/blog/default.jpg'}
                    alt={post.title}
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Chip
                        label={post.category}
                        color="primary"
                        size="small"
                        sx={{ 
                          mb: 1.5,
                          fontWeight: 500,
                          backgroundColor: '#755985',
                          '&:hover': {
                            backgroundColor: '#7c5ebd'
                          }
                        }}
                      />
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: '#755985',
                          mb: 1
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.5,
                          fontSize: '0.875rem'
                        }}
                      >
                        {post.summary}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={post.author?.avatar || '/images/avatars/default.jpg'}
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1.5,
                          border: '1.5px solid #755985'
                        }}
                      />
                      <Box>
                        <Typography 
                          variant="subtitle2"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {post.author?.name || 'Coach'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {format(new Date(post.createdAt), 'MMM d, yyyy')} · {post.readTime} minute read
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      component={Link}
                      to={`/blog/${post._id}`}
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="small"
                      sx={{
                        py: 1,
                        backgroundColor: '#755985',
                        '&:hover': {
                          backgroundColor: '#7c5ebd'
                        }
                      }}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" align="center">
                No blog posts available.
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Navigation Arrows */}
        {posts.length > postsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <IconButton
              sx={{
                mx: 1,
                backgroundColor: '#755985',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#7c5ebd',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(117, 89, 133, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)'
                },
                padding: '6px'
              }}
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              size="small"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              sx={{
                mx: 1,
                backgroundColor: '#755985',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#7c5ebd',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(117, 89, 133, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)'
                },
                padding: '6px'
              }}
              onClick={handleNextPage}
              disabled={currentPage >= totalPages - 1}
              size="small"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Blog;
